import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { updateUserRole, updateUserStatus } from '../../components/admin/adminApiService';
import { userManagementApi } from '../../components/admin/userManagementApi';
import ViewUserModal from '../../components/admin/modals/ViewUserModal';
import toast from 'react-hot-toast';

const AdminUsers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showResetWalletsModal, setShowResetWalletsModal] = useState(false);
  const [isResettingWallets, setIsResettingWallets] = useState(false);

  // Load users data with pagination
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        const response = await userManagementApi.getUsers({
          page: currentPage,
          limit: 20,
          search: searchTerm,
          role: filterRole,
          status: filterStatus,
          verified: filterVerified,
          sortBy,
          sortOrder
        });
        
        setUsers(response.users || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalCount(response.pagination?.totalCount || 0);
      } catch (error) {
        console.error('Error loading users:', error);
        toast.error('Failed to load users');
        setUsers([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [currentPage, searchTerm, filterRole, filterStatus, filterVerified, sortBy, sortOrder]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRole, filterStatus, filterVerified, sortBy, sortOrder]);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#059669';
      case 'pending': return '#d97706';
      case 'suspended': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'ADMIN': return '#7c3aed';
      case 'USER': return '#2563eb';
      default: return '#6b7280';
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      // Update the user in the local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      toast.success('User role updated successfully');
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error(error.message || 'Failed to update user role');
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await updateUserStatus(userId, newStatus);
      // Update the user in the local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, isActive: newStatus } : user
      ));
      toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error(error.message || 'Failed to update user status');
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedUser(null);
  };

  const handleResetWallets = async () => {
    try {
      setIsResettingWallets(true);
      const response = await userManagementApi.resetAllWallets();
      toast.success(response.message || 'All wallet balances reset to $400');
      setShowResetWalletsModal(false);
      
      // Reload users to show updated balances
      const updatedResponse = await userManagementApi.getUsers({
        page: currentPage,
        limit: 20,
        search: searchTerm,
        role: filterRole,
        status: filterStatus,
        verified: filterVerified,
        sortBy,
        sortOrder
      });
      setUsers(updatedResponse.users || []);
    } catch (error) {
      console.error('Error resetting wallets:', error);
      toast.error(error.message || 'Failed to reset wallet balances');
    } finally {
      setIsResettingWallets(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-users">
          <div className="admin-card">
            <div className="loading-container">
              <div className="admin-loading-spinner">
                <div className="admin-spinner"></div>
              </div>
              <p>Loading users...</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-users">
        <div className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">User Management</h2>
            <p className="admin-card-subtitle">Manage system users and permissions ({totalCount} total)</p>
          </div>
        </div>

        {/* Filters */}
        <div className="admin-card">
          <div className="admin-filters">
            <div className="admin-form-group">
              <label className="admin-form-label">Search Users</label>
              <input
                type="text"
                className="admin-form-input"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Filter by Role</label>
              <select
                className="admin-form-select"
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
              >
                <option value="all">All Roles</option>
                <option value="USER">Users</option>
                <option value="ADMIN">Admins</option>
              </select>
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Filter by Status</label>
              <select
                className="admin-form-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Filter by Verification</label>
              <select
                className="admin-form-select"
                value={filterVerified}
                onChange={(e) => setFilterVerified(e.target.value)}
              >
                <option value="all">All Verification</option>
                <option value="true">Verified</option>
                <option value="false">Unverified</option>
              </select>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Users ({users.length} of {totalCount})</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="admin-btn admin-btn-warning"
                onClick={() => setShowResetWalletsModal(true)}
                title="Reset all user wallet balances to $400"
              >
                💰 Reset All Wallets
              </button>
              <button className="admin-btn admin-btn-primary">
                ➕ Add New User
              </button>
            </div>
          </div>
          
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th 
                    className="sortable" 
                    onClick={() => handleSort('firstName')}
                    style={{ cursor: 'pointer' }}
                  >
                    Name {sortBy === 'firstName' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="sortable" 
                    onClick={() => handleSort('email')}
                    style={{ cursor: 'pointer' }}
                  >
                    Email {sortBy === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="sortable" 
                    onClick={() => handleSort('role')}
                    style={{ cursor: 'pointer' }}
                  >
                    Role {sortBy === 'role' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Status</th>
                  <th>Verified</th>
                  <th>Wallet Balance</th>
                  <th>Bets</th>
                  <th>All Bets</th>
                  <th 
                    className="sortable" 
                    onClick={() => handleSort('createdAt')}
                    style={{ cursor: 'pointer' }}
                  >
                    Join Date {sortBy === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A'}</td>
                    <td>{user.email}</td>
                    <td>
                      <select
                        className="admin-form-select admin-inline-select admin-role-select"
                        value={user.role || 'USER'}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        style={{ 
                          backgroundColor: getRoleColor(user.role),
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          fontSize: '13px',
                          fontWeight: '600',
                          minWidth: '80px',
                          textAlign: 'center'
                        }}
                      >
                        <option value="USER">USER</option>
                        <option value="ADMIN">ADMIN</option>
                      </select>
                    </td>
                    <td>
                      <select
                        className="admin-form-select admin-inline-select admin-status-select"
                        value={user.isActive ? 'active' : 'inactive'}
                        onChange={(e) => handleStatusChange(user.id, e.target.value === 'active')}
                        style={{ 
                          backgroundColor: user.isActive ? '#059669' : '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          fontSize: '13px',
                          fontWeight: '600',
                          minWidth: '90px',
                          textAlign: 'center'
                        }}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </td>
                    <td>
                      <span 
                        className="admin-badge"
                        style={{ backgroundColor: user.isVerified ? '#059669' : '#d97706' }}
                      >
                        {user.isVerified ? 'Verified' : 'Unverified'}
                      </span>
                    </td>
                    <td>
                      {user.wallet ? 
                        `$${parseFloat(user.wallet.balance || 0).toFixed(2)} ${user.wallet.currency || 'USD'}` : 
                        'No Wallet'
                      }
                    </td>
                    <td>{user.activeTournamentBetsCount ?? user.bets?.length ?? 0}</td>
                    <td>{user._count?.bets || 0}</td>
                    <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</td>
                    <td>
                      <div className="admin-actions">
                        {/*<button className="admin-btn admin-btn-small admin-btn-secondary">
                          Edit
                        </button>*/}
                        <button 
                          className="admin-btn admin-btn-small admin-btn-secondary"
                          onClick={() => handleViewUser(user)}
                        >
                          View
                        </button>
                        <button className="admin-btn admin-btn-small admin-btn-danger">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="admin-pagination">
              <div className="admin-pagination-info">
                Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, totalCount)} of {totalCount} users
              </div>
              <div className="admin-pagination-controls">
                <button
                  className="admin-btn admin-btn-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                
                <div className="admin-pagination-pages">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        className={`admin-btn admin-btn-pagination ${
                          currentPage === pageNum ? 'active' : ''
                        }`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  className="admin-btn admin-btn-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* User Stats */}
        <div className="admin-stats">
          <div className="admin-stat-card">
            <div className="admin-stat-icon">👥</div>
            <div className="admin-stat-value">{totalCount}</div>
            <div className="admin-stat-label">Total Users</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon">✅</div>
            <div className="admin-stat-value">{users.filter(u => u.isActive).length}</div>
            <div className="admin-stat-label">Active Users (This Page)</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon">✔️</div>
            <div className="admin-stat-value">{users.filter(u => u.isVerified).length}</div>
            <div className="admin-stat-label">Verified Users (This Page)</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon">👑</div>
            <div className="admin-stat-value">{users.filter(u => u.role === 'ADMIN').length}</div>
            <div className="admin-stat-label">Admins (This Page)</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon">💳</div>
            <div className="admin-stat-value">{users.filter(u => u.wallet).length}</div>
            <div className="admin-stat-label">With Wallets (This Page)</div>
          </div>
        </div>
      </div>

      {/* View User Modal */}
      {showViewModal && selectedUser && (
        <ViewUserModal
          user={selectedUser}
          onClose={handleCloseViewModal}
        />
      )}

      {/* Reset Wallets Confirmation Modal */}
      {showResetWalletsModal && (
        <div className="admin-modal-overlay" onClick={() => !isResettingWallets && setShowResetWalletsModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>⚠️ Reset All Wallet Balances</h3>
              <button 
                className="admin-modal-close" 
                onClick={() => setShowResetWalletsModal(false)}
                disabled={isResettingWallets}
              >
                ×
              </button>
            </div>
            <div className="admin-modal-body">
              <p style={{ marginBottom: '15px' }}>
                Are you sure you want to reset ALL user wallet balances to <strong>$400</strong>?
              </p>
              <p style={{ color: '#d97706', marginBottom: '15px' }}>
                ⚠️ This action will affect all users in the system and cannot be undone.
              </p>
              <p style={{ fontSize: '14px', color: '#059669', marginBottom: '10px' }}>
                ✓ The Proskins System wallet will NOT be affected.
              </p>
              <p style={{ fontSize: '14px', color: '#6b7280' }}>
                Current users: <strong>{totalCount}</strong>
              </p>
            </div>
            <div className="admin-modal-footer">
              <button 
                className="admin-btn admin-btn-secondary"
                onClick={() => setShowResetWalletsModal(false)}
                disabled={isResettingWallets}
              >
                Cancel
              </button>
              <button 
                className="admin-btn admin-btn-warning"
                onClick={handleResetWallets}
                disabled={isResettingWallets}
              >
                {isResettingWallets ? 'Resetting...' : 'Yes, Reset All Wallets'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminUsers;
